import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { toast } from 'sonner';
import { ModalShell } from '../ui/modal-shell';
import { logger } from '../../utils/logger';
import { useTimezone } from '../../contexts/TimezoneContext';
import { todayIST } from '../../utils/locale';
import TemplateFillInForm, { fillStakeholder, computeOffsetDate } from './TemplateFillInForm';
import { ITEM_TYPE_CONFIG } from './commitmentConstants';

const COMPANION_ITEMS = [
  { itemType: 'reading', title: 'Review prep materials', url: '', include: true },
  { itemType: 'note', title: 'Discussion points — {stakeholder}', description: 'Agenda, key questions, decisions to drive.', include: true },
  { itemType: 'call', title: 'Share the outcome', contactName: '', include: true },
  { itemType: 'task', title: 'Send recap + agreed next steps to {stakeholder}', priority: 'HIGH', estimatedMinutes: 15, dueDateOffsetDays: 1, include: true },
];

const inputStyle = {
  background: 'var(--pd-panel-solid)',
  border: '1px solid var(--pd-border)',
  color: 'var(--pd-text)',
};

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[9px] uppercase tracking-wider font-semibold block mb-1" style={{ color: 'var(--pd-subtle)' }}>{label}</label>
      {children}
    </div>
  );
}

/**
 * Promote an existing meeting (time block) or task into a commitment,
 * spawning companion items (reading, note, follow-up call, recap task) around it.
 * `source`: { type: 'event'|'task', id, title, date?, businessId? }
 */
export default function ConvertToCommitmentModal({ source, onClose, onCreated }) {
  const navigate = useNavigate();
  const { timezone: tzid } = useTimezone();
  const [form, setForm] = useState({ title: source.title || '', stakeholder: '', deadline: source.date || '' });
  const [items, setItems] = useState(COMPANION_ITEMS.map((it) => ({ ...it })));
  const [submitting, setSubmitting] = useState(false);

  const anchorCfg = ITEM_TYPE_CONFIG[source.type] || ITEM_TYPE_CONFIG.task;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const filledItems = items
        .filter((it) => it.include)
        .map(({ include, ...spec }) => {
          const out = { ...spec };
          for (const f of ['title', 'description', 'contactName']) {
            if (out[f]) out[f] = fillStakeholder(out[f], form.stakeholder);
          }
          if (spec.itemType === 'event' && !out.date) {
            out.date = computeOffsetDate(form.deadline, spec.dueDateOffsetDays) || todayIST(tzid);
          }
          return out;
        });
      const res = await api.post('/commitments/convert', {
        sourceType: source.type,
        sourceId: source.id,
        ...form,
        businessId: source.businessId || undefined,
        items: filledItems,
      });
      toast.success(`Commitment created with ${res.data.linkedCount} linked items`, {
        action: { label: 'View', onClick: () => navigate('/commitments') },
      });
      onCreated?.(res.data.commitmentId);
      onClose();
    } catch (err) {
      logger.error('Convert to commitment failed:', err);
      toast.error(err?.response?.status === 409 ? 'Already part of a commitment' : 'Could not convert');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      open
      onClose={onClose}
      title="Convert to Commitment"
      subtitle="Build the full case file around this item"
      testId="convert-commitment-modal"
      maxWidth={560}
    >
      <div className="p-5 space-y-3">
        {/* Anchor item */}
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl" style={{ background: `${anchorCfg.color}0A`, border: `1px solid ${anchorCfg.color}30` }}>
          <anchorCfg.icon className="w-4 h-4 flex-shrink-0" style={{ color: anchorCfg.color }} />
          <div className="min-w-0 flex-1">
            <div className="text-[8px] uppercase font-bold tracking-wider" style={{ color: anchorCfg.color }}>Anchor — existing {anchorCfg.label.toLowerCase()}</div>
            <div className="text-[12px] font-semibold truncate" style={{ color: 'var(--pd-text)' }}>{source.title}</div>
          </div>
          {source.date && <span className="text-[10px] tabular-nums flex-shrink-0" style={{ color: 'var(--pd-muted)' }}>{source.date}</span>}
        </div>

        <Field label="Commitment title">
          <input
            data-testid="convert-title-input"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            className="w-full px-3 py-2 rounded-xl text-[12px] outline-none"
            style={inputStyle}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Stakeholder (auto-fills item titles)">
            <input
              data-testid="convert-stakeholder-input"
              value={form.stakeholder}
              onChange={(e) => setForm((f) => ({ ...f, stakeholder: e.target.value }))}
              placeholder="e.g., John"
              className="w-full px-3 py-2 rounded-xl text-[12px] outline-none"
              style={inputStyle}
            />
          </Field>
          <Field label="Deadline (anchors dates)">
            <input
              data-testid="convert-deadline-input"
              type="date"
              value={form.deadline}
              onChange={(e) => setForm((f) => ({ ...f, deadline: e.target.value }))}
              className="w-full px-3 py-2 rounded-xl text-[12px] outline-none"
              style={inputStyle}
            />
          </Field>
        </div>

        <TemplateFillInForm
          items={items}
          onChange={setItems}
          stakeholder={form.stakeholder}
          deadline={form.deadline}
        />

        <div className="flex items-center justify-between pt-1">
          <span className="text-[10px]" style={{ color: 'var(--pd-subtle)' }}>
            Anchor + {items.filter((i) => i.include).length} companion items
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-[12px] font-medium"
              style={{ color: 'var(--pd-muted)' }}
            >
              Cancel
            </button>
            <button
              data-testid="convert-submit-btn"
              disabled={submitting || !form.title.trim()}
              onClick={handleSubmit}
              className="px-5 py-2 rounded-xl text-[12px] font-semibold text-white"
              style={{ background: 'var(--pd-focus)' }}
            >
              {submitting ? 'Converting...' : 'Create commitment'}
            </button>
          </div>
        </div>
      </div>
    </ModalShell>
  );
}
